"use client";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import {
  Box,
  BoxProps,
  CloseButton,
  Collapse,
  Drawer,
  DrawerContent,
  Flex,
  FlexProps,
  HStack,
  Icon,
  IconButton,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { isArray, trim } from "lodash";
import { ComponentType, ReactNode, forwardRef, useMemo } from "react";
import { IconType } from "react-icons";
import { FiMenu } from "react-icons/fi";
import Link from "../../Link";

export interface IRoute {
  path: string;
  name: string;
  layout?: string;
  component?: ComponentType;
  icon?: IconType;
  collapse?: boolean;
  items?: IRoute[];
  /** ACT slug gating this nav entry; enforced by the app's route filters. */
  act?: string;
}

interface NavItemProps extends FlexProps {
  icon?: IconType;
  link: string;
  children: React.ReactNode;
  items?: IRoute[];
  onClose: () => void;
}

interface MobileProps extends Omit<FlexProps, "children"> {
  onOpen: () => void;
  children?: ReactNode;
  logo?: ReactNode;
}

interface SidebarProps extends BoxProps {
  menuItems: IRoute[];
  onClose: () => void;
  logo?: ReactNode;
}

interface SidebarWithHeaderProps {
  menuItems: IRoute[];
  logo?: ReactNode;
  headerNavItem?: ReactNode;
  children: ReactNode;
  sideBarContentProps?: BoxProps;
}

interface NavLinkItemProps extends Omit<FlexProps, "children"> {
  hasItems: boolean;
  icon?: IconType;
  children?: ReactNode;
  isOpen?: boolean;
  isActive?: boolean;
}

const NavLinkItem = ({
  hasItems,
  icon,
  children,
  isActive,
  isOpen,
  ...rest
}: NavLinkItemProps) => {
  return (
    <Flex
      align="center"
      p="2"
      px="4"
      mx="4"
      borderRadius="lg"
      role="group"
      cursor="pointer"
      color={isActive ? "primary.600" : "inherit"}
      _active={{
        backgroundColor: "whiteAlpha.100",
      }}
      background={isActive && !hasItems ? "whiteAlpha.50" : "inherit"}
      _hover={{
        backgroundColor: "whiteAlpha.50",
      }}
      {...rest}
    >
      {icon && (
        <Icon
          mr="4"
          fontSize="16"
          _groupHover={{
            color: "white",
          }}
          as={icon}
        />
      )}
      <>{children}</>

      {hasItems && (
        <Icon
          ml="auto"
          fontSize="2xl"
          as={isOpen ? FaChevronDown : FaChevronRight}
        />
      )}
    </Flex>
  );
};

const NavItem = ({ icon, children, link, items, onClose }: NavItemProps) => {
  const hasItems = items && isArray(items) && items?.length > 0;
  const locationPath = window?.location?.pathname;
  const isActive = useMemo(
    () =>
      link === "/"
        ? locationPath === link
        : hasItems
          ? locationPath?.includes(link)
          : trim(locationPath, "/") === trim(link, "/"),
    [link, locationPath, hasItems],
  );

  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: isActive,
  });

  return (
    <>
      {hasItems ? (
        <NavLinkItem
          isActive={isActive}
          isOpen={isOpen}
          onClick={onToggle}
          hasItems={Boolean(hasItems)}
          icon={icon}
        >
          {children}
        </NavLinkItem>
      ) : (
        <Link
          href={link}
          style={{ textDecoration: "none" }}
          _focus={{ boxShadow: "none" }}
          onClick={onClose}
        >
          <NavLinkItem
            isActive={isActive}
            hasItems={Boolean(hasItems)}
            icon={icon}
          >
            {children}
          </NavLinkItem>
        </Link>
      )}

      {hasItems && (
        <Collapse in={isOpen && hasItems} delay={5000}>
          <Box
            sx={{
              pl: "1rem",
            }}
          >
            {items.map((iLink) => (
              <NavItem
                key={iLink.name}
                icon={iLink.icon}
                link={iLink.path}
                items={iLink.items}
                onClose={onClose}
              >
                {iLink.name}
              </NavItem>
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
};

const SidebarContent = forwardRef<HTMLDivElement, SidebarProps>(
  ({ onClose, menuItems, logo, ...rest }, ref) => {
    return (
      <Box
        ref={ref}
        transition={{
          base: "1s ease",
          md: "none",
        }}
        bg={useColorModeValue("white", "gray.900")}
        borderRight="1px"
        borderRightColor={useColorModeValue("gray.200", "gray.700")}
        w={{ base: "full" }}
        maxW={{ base: "xs", md: "16rem" }}
        pos="fixed"
        h="full"
        maxH="100vh"
        overflowY="auto"
        {...rest}
      >
        <Flex h="20" alignItems="center" mx="8" justifyContent="space-between">
          {logo ? (
            <>{logo}</>
          ) : (
            <Text
              color="black"
              fontSize="2xl"
              fontFamily="monospace"
              fontWeight="bold"
            >
              PVPanel
            </Text>
          )}
          <CloseButton
            display={{ base: "flex", md: "none" }}
            onClick={onClose}
          />
        </Flex>
        {menuItems.map((link) => (
          <NavItem
            key={link.name}
            icon={link.icon}
            link={link.path}
            items={link.items}
            onClose={onClose}
          >
            {link.name}
          </NavItem>
        ))}
      </Box>
    );
  },
);

const MobileNav = ({ onOpen, children, logo, ...rest }: MobileProps) => {
  return (
    <Flex
      ml={{ base: 0, md: 60 }}
      px={{ base: 4, md: 4 }}
      height="20"
      alignItems="center"
      bg={useColorModeValue("white", "gray.900")}
      borderBottomWidth="1px"
      borderBottomColor={useColorModeValue("gray.200", "gray.700")}
      justifyContent={{ base: "space-between", md: "flex-end" }}
      {...rest}
    >
      <IconButton
        display={{ base: "flex", md: "none" }}
        onClick={onOpen}
        variant="outline"
        aria-label="open menu"
        icon={<FiMenu />}
      />
      <Box display={{ base: "flex", md: "none" }}>
        {logo ? (
          <>{logo}</>
        ) : (
          <Text
            color="white"
            fontSize="2xl"
            fontFamily="monospace"
            fontWeight="bold"
          >
            PVPanel
          </Text>
        )}
      </Box>

      <HStack spacing={{ base: "0", md: "6" }}>
        <>{children}</>
      </HStack>
    </Flex>
  );
};

export const SidebarWithHeader = ({
  headerNavItem,
  logo,
  sideBarContentProps,
  menuItems,
  children,
}: SidebarWithHeaderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const sideBarWidth = useBreakpointValue(
    {
      base: "full",
      md: "16rem",
    },
    {
      fallback: "md",
    },
  );

  return (
    <Box minH="100vh">
      <SidebarContent
        menuItems={menuItems}
        logo={logo}
        {...sideBarContentProps}
        maxW={sideBarWidth}
        onClose={() => onClose}
        display={{ base: "none", md: "block" }}
      />
      <Drawer
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent menuItems={menuItems} onClose={onClose} />
        </DrawerContent>
      </Drawer>
      {/* mobilenav */}
      <MobileNav onOpen={onOpen} logo={logo}>
        {headerNavItem}
      </MobileNav>

      <Box
        p="4"
        sx={{
          ml: sideBarWidth,
        }}
      >
        <>{children}</>
      </Box>
    </Box>
  );
};
